export class SDKError extends Error {
	constructor(message, cause) {
		super(message);
		this.name = 'SDKError';
		this.cause = cause;

		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, SDKError);
		}
	}
}

export class APIError extends Error {
	constructor(status, message, response) {
		super(message);
		this.name = 'APIError';
		this.status = status;
		this.response = response;

		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, APIError);
		}
	}

	isNetworkError() {
		return this.status === 0;
	}

	isClientError() {
		return this.status >= 400 && this.status < 500;
	}

	isServerError() {
		return this.status >= 500 && this.status < 600;
	}
}

// Backend rejects every widget write from a blocked contact with
// 403 + this code (support side: live chat; feedback side: posts, surveys).
export const CONTACT_BLOCKED_CODE = 'CONTACT_BLOCKED';
export const CONTACT_BLOCKED_MESSAGE = 'Your access has been restricted';

export function isContactBlockedError(error) {
	return (
		error?.status === 403 && error?.response?.code === CONTACT_BLOCKED_CODE
	);
}

// Backend rejects a message on a resolved conversation with 409 + this code.
export const CONVERSATION_CLOSED_CODE = 'CONVERSATION_CLOSED';

export function isConversationClosedError(error) {
	return error?.response?.code === CONVERSATION_CLOSED_CODE;
}

export class WidgetError extends Error {
	constructor(message, widgetType, widgetId) {
		super(message);
		this.name = 'WidgetError';
		this.widgetType = widgetType;
		this.widgetId = widgetId;

		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, WidgetError);
		}
	}
}

export class ConfigError extends Error {
	constructor(message, configKey) {
		super(message);
		this.name = 'ConfigError';
		this.configKey = configKey;

		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, ConfigError);
		}
	}
}

export class ValidationError extends Error {
	constructor(message, field, value) {
		super(message);
		this.name = 'ValidationError';
		this.field = field;
		this.value = value;

		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, ValidationError);
		}
	}
}

export class ErrorHandler {
	constructor(debug = false) {
		this.debug = debug;
	}

	handle(error, context = '') {
		const errorInfo = {
			name: error.name,
			message: error.message,
			context,
			timestamp: new Date().toISOString(),
		};

		if (error instanceof APIError) {
			errorInfo.status = error.status;
			errorInfo.type = 'api';
		} else if (error instanceof WidgetError) {
			errorInfo.widgetType = error.widgetType;
			errorInfo.widgetId = error.widgetId;
			errorInfo.type = 'widget';
		} else if (error instanceof ConfigError) {
			errorInfo.configKey = error.configKey;
			errorInfo.type = 'config';
		} else if (error instanceof ValidationError) {
			errorInfo.field = error.field;
			errorInfo.value = error.value;
			errorInfo.type = 'validation';
		} else {
			errorInfo.type = 'unknown';
		}

		if (this.debug) {
			console.error('[Product7 Error]', errorInfo, error);
		} else {
			console.error('[Product7 Error]', error.message);
		}

		return errorInfo;
	}

	getUserMessage(error) {
		if (error instanceof APIError) {
			if (error.isNetworkError()) {
				return 'Network error. Please check your connection and try again.';
			} else if (error.isClientError()) {
				return 'Invalid request. Please check your input and try again.';
			} else if (error.isServerError()) {
				return 'Server error. Please try again later.';
			}
			return 'Failed to submit feedback. Please try again.';
		}

		if (error instanceof ValidationError) {
			return `Please check your ${error.field}: ${error.message}`;
		}

		if (error instanceof ConfigError) {
			return 'Configuration error. Please check your SDK setup.';
		}

		if (error instanceof WidgetError) {
			return 'Widget error. Please try refreshing the page.';
		}

		return 'An unexpected error occurred. Please try again.';
	}
}
